import styled from '@emotion/styled';
import { isObject } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonStepButtons } from '../components/CommonStepButtons';
import { FloatingLabel } from '../components/FloatingLabel';
import { QuestionWrapper } from '../components/QuestionWrapper';
import { ReviewInputFullText } from '../components/ReviewInputFullText';
import { ReviewInputMulti } from '../components/ReviewInputMulti';
import { ReviewInputNumerical } from '../components/ReviewInputNumerical';
import { ScreenTitle } from '../components/ScreenTitle';
import { ScreenWrapper } from '../components/ScreenWrapper';
import { IntakeFormProps, questionDataMap } from '../models/intakeForm';

const QuestionDataOrder = [2, 3, 4, 5, 6, 7, 8];

const MultiInputSpacer = styled('div')({
    marginBottom: 12,
    ':last-of-type': { marginBottom: -6 },
});

export const IntakeReviewScreen: React.FC<IntakeFormProps> = ({
    allResponses,
    goBack,
    saveMultipleResponses,
}) => {
    // We are storing responses by screen before this for convenience, but now we'll map them to
    // questionId so we can submit them easily.
    const [responseObj, setResponseObj] = useState(
        Object.entries(allResponses).reduce((accum, [key, value]) => {
            const mappedKey = questionDataMap[key].questionId || key;

            if (Array.isArray(value)) {
                return { ...accum, [mappedKey]: value };
            }

            if (isObject(value)) {
                return { ...accum, ...value };
            }

            return {
                ...accum,
                [mappedKey]: value,
            };
        }, {}),
    );
    const { t } = useTranslation();

    const handleResponseChange = (questionId, response) => {
        setResponseObj({
            ...responseObj,
            [questionId]: response,
        });
    };

    const handleSubmitToCoach = () => {
        saveMultipleResponses(responseObj, true);
    };

    return (
        <ScreenWrapper>
            <QuestionWrapper scrollContent additionalVerticalPadding>
                <ScreenTitle>{t('intakeForms:review.title')}</ScreenTitle>
                {QuestionDataOrder.map(index => {
                    const {
                        questionId,
                        questionIds,
                        questionText,
                        questionPlaceholder,
                    } = questionDataMap[index];

                    if (questionId) {
                        // If questionId exists then the screen has a single question
                        const inputProps = {
                            key: questionId,
                            questionId,
                            questionText,
                            questionPlaceholder,
                            response: responseObj[questionId],
                            handleResponseChange,
                        };

                        if (index === 3) {
                            // This is a special case; the multiselect with predetermined answers
                            return <ReviewInputMulti {...inputProps} />;
                        }

                        return <ReviewInputFullText {...inputProps} />;
                    }

                    // If we reach here, then questionIds should exist, indicating that the screen has
                    // multiple questions per screen
                    return (
                        <div key={questionIds.join('_')}>
                            <FloatingLabel>{t(questionText)}</FloatingLabel>
                            {questionIds.map(({ subQuestionId, subQuestionTitle }) => {
                                const subInputProps = {
                                    key: subQuestionId,
                                    questionId: subQuestionId,
                                    questionPlaceholder,
                                    response: responseObj[subQuestionId],
                                    handleResponseChange,
                                };

                                // index 6 is the screen with 3 free text goals
                                if (index === 6) {
                                    return (
                                        <MultiInputSpacer key={subQuestionId}>
                                            <ReviewInputFullText {...subInputProps} />
                                        </MultiInputSpacer>
                                    );
                                }

                                return (
                                    // eslint-disable-next-line react/jsx-key
                                    <ReviewInputNumerical
                                        {...subInputProps}
                                        questionTitle={subQuestionTitle}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
                <CommonStepButtons
                    forwardText={t('intakeForms:review.submit_to_coach')}
                    onForwardClick={handleSubmitToCoach}
                    onBackClick={goBack}
                    onSkipClick={() => null}
                    skipText={null}
                />
            </QuestionWrapper>
        </ScreenWrapper>
    );
};
